import { FC } from 'react';
import styled from 'styled-components';
import { CollapsibleMenuItem } from '@components/layout/Sidebar/components/CollapsibleMenuItem';
import ProtectedRenderer from '@components/ProtectedRenderer';
import {
  canShowBySettings,
  MenuItem,
  SettingsManager,
  useSidebarStructure
} from '@components/layout/Sidebar/useSidebarStructure';
import { Collapse } from 'antd';

const Panel = styled(Collapse.Panel)`
  border: 0 !important;
  margin-bottom: 8px !important;

  .ant-collapse-header {
    padding: 0 !important;
  }

  .ant-collapse-content {
    border: 0 !important;
  }
  .ant-collapse-content-box {
    padding: 8px 0 !important;
  }
`;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  overflow: hidden auto;
  flex: 1;
  min-height: 0;
  margin: 8px 0;
  .ant-collapse {
    border: 0 !important;
    background-color: transparent !important;
  }
`;

export const Sidebar: FC<{ className?: string; mobile?: boolean; minimized?: boolean }> = ({ minimized, className, mobile }) => {
  const menuStructure = useSidebarStructure(mobile);

  const renderMenuItem = (item: MenuItem, index: number) => {
    if (item.withProtectedRenderer) {
      return (
        <ProtectedRenderer key={index} allowedUserTypes={item.withProtectedRenderer}>
          <SettingsManager settings={item.settings}>{item.component}</SettingsManager>
        </ProtectedRenderer>
      );
    }

    return (
      <SettingsManager settings={item.settings} key={index}>
        {item.component}
      </SettingsManager>
    );
  };

  if (mobile) {
    return (
      <Container className={className}>
        <Collapse
          accordion
          ghost
          expandIconPosition="end"
          expandIcon={props => {
            return <Icon className="icon-chevron-right" isActive={props.isActive}></Icon>;
          }}
        >
          {menuStructure.map((entry, index) => {
            if (entry.type === 'rootLink') {
              return renderMenuItem(
                {
                  ...entry.item,
                  name: entry.item.title,
                  component: (
                    <CollapsibleMenuItem
                      isMobile
                      to={entry.item.to}
                      title={entry.item.title}
                      key={index}
                      icon={entry.item.icon}
                      paths={entry.item?.regex ? [entry.item.regex] : [entry.item.to]}
                      badge={entry.item.badge}
                    />
                  )
                },
                index
              );
            }
            if (entry.type === 'single') {
              return renderMenuItem(entry.item, index);
            } else if (entry.type === 'grouped') {
              const paths = entry.items.map(item => item.regex || item.name);
              const notEmpty = entry.items.some(item => item.allowed && canShowBySettings(item.settings));
              if (notEmpty) {
                return (
                  <Panel
                    key={index}
                    header={<CollapsibleMenuItem isMobile title={entry.title} key={index} icon={entry.icon} paths={paths} />}
                  >
                    {entry.items.map((item, itemIndex) => renderMenuItem(item, itemIndex))}
                  </Panel>
                );
              }
            }
          })}
        </Collapse>
      </Container>
    );
  }
  return (
    <Container className={className}>
      {menuStructure.map((entry, index) => {
        if (entry.type === 'rootLink') {
          return (
            <CollapsibleMenuItem
              to={entry.item.to}
              title={entry.item.title}
              key={index}
              icon={entry.item.icon}
              paths={entry.item?.regex ? [entry.item.regex] : [entry.item.to]}
              badge={entry.item.badge}
            />
          );
        }
        if (entry.type === 'single') {
          return renderMenuItem(entry.item, index);
        } else if (entry.type === 'grouped') {
          const paths = entry.items.map(item => item.regex || item.name);
          const notEmpty = entry.items.some(item => item.allowed && canShowBySettings(item.settings));
          if (notEmpty) {
            return (
              <CollapsibleMenuItem title={entry.title} key={index} icon={entry.icon} paths={paths}>
                {entry.items.map((item, itemIndex) => renderMenuItem(item, itemIndex))}
              </CollapsibleMenuItem>
            );
          }
        }
      })}
    </Container>
  );
};

const Icon = styled.i<{ isActive?: boolean }>`
  font-size: 18px !important;
  color: var(--gray-7) !important;
  z-index: 99;
  transform: rotate(${props => (props.isActive ? -90 : 90)}deg) translate(${props => (props.isActive ? '50%, 0' : '-50%, 0')}) !important;
`;
